import {
    DataGrid,
    GridColumnsConfigProvider,
    useErrorState,
    WorkplaceBtnActions,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getOrderAgreementProlongations } from 'axios/orderApi';
import DataTimeText from 'components/Common/DataTime';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { LoanAgreementProlongation, OrderAgreementProlongations } from 'models/api/order';
import { useOrderInfoContext } from 'providers/OrderInfoProvider';
import { RefObject, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Card, CardHeader } from 'reactstrap';
import ProlongationAgreementAddPopover from '../../Popovers/ProlongationAgreementAddPopover';
import ProlongationAgreementDeletePopover from '../../Popovers/ProlongationAgreementDeletePopover';
import '../OrderInfo.scss';

const AgreementProlongations = ({
    wasOpened,
    container,
}: {
    wasOpened: boolean;
    container?: RefObject<HTMLElement>;
}) => {
    const api = useApi();
    const { hasRole } = useAuth();
    const { t } = useTranslation();
    const { orderDetails } = useOrderInfoContext();
    const { handleRequestError } = useErrorState();

    const [data, setData] = useState<OrderAgreementProlongations | null>(null);

    const { isFetching } = useQuery(
        ['orderAgreementProlongations', orderDetails?.id],
        () =>
            orderDetails?.id ? getOrderAgreementProlongations(api, orderDetails?.id) : undefined,
        {
            onSuccess: (res) => res && setData(res),
            enabled: wasOpened,
            onError: (err) => {
                handleRequestError(err);
            },
        }
    );

    return data &&
        data.loan &&
        (data.loan.statusCode === 'active' ||
            data.loan.statusCode === 'outloanterm' ||
            (data.prolongationAgreements && data.prolongationAgreements.length > 0)) ? (
        <>
            <Card className="card-for-table">
                <CardHeader>
                    <div className="d-flex justify-content-between">
                        <div>{t('BackOfficeAgreementToProlongation')}</div>
                        <div>
                            {(data.loan.statusCode === 'active' ||
                                data.loan.statusCode === 'outloanterm') &&
                                data.loan.id &&
                                hasRole('orderAddAgreementProlongation') && (
                                    <ProlongationAgreementAddPopover
                                        loanId={data.loan.id}
                                        refetchQueryKey={['orderAgreementProlongations']}
                                    />
                                )}
                        </div>
                    </div>
                </CardHeader>

                <GridColumnsConfigProvider<LoanAgreementProlongation>
                    storagePath="order-agreement-prolongation-grid"
                    initColumns={[
                        {
                            key: 'id',
                            draggable: false,
                            name: <Trans i18nKey={'BackOfficeId'} />,
                        },
                        {
                            key: 'sd',
                            draggable: false,
                            name: <Trans i18nKey={'BackOfficeConsentDate'} />,
                            formatter: ({ row }) =>
                                row.dtDelete ? (
                                    <del>
                                        <DataTimeText value={row.sd} />
                                    </del>
                                ) : (
                                    <DataTimeText value={row.sd} />
                                ),
                        },
                        {
                            key: 'userCreate',
                            draggable: false,
                            name: <Trans i18nKey={'BackOfficeOperator'} />,
                            formatter: ({ row }) => (
                                <>{row.dtDelete ? row.userDelete : row.userCreate}</>
                            ),
                        },
                        {
                            key: 'commentCreate',
                            draggable: false,
                            name: <Trans i18nKey={'BackOfficeComment'} />,
                            formatter: ({ row }) => (
                                <>{row.dtDelete ? row.commentDelete : row.commentCreate}</>
                            ),
                        },
                        {
                            key: 'dtCreate',
                            draggable: false,
                            name: <Trans i18nKey={'BackOfficeModificationDate'} />,
                            formatter: ({ row }) => (
                                <DataTimeText value={row.dtDelete ? row.dtDelete : row.dtCreate} />
                            ),
                        },
                        {
                            key: 'dt',
                            draggable: false,
                            name: <Trans i18nKey={'BackOfficeRenewalStartDate'} />,
                            formatter: ({ row }) => <DataTimeText value={row.dt} />,
                        },
                        {
                            key: 'term',
                            draggable: false,
                            name: <Trans i18nKey={'BackOfficeTerm'} />,
                        },
                        {
                            key: 'actions',
                            name: <FontAwesomeIcon icon={faBoltLightning} />,
                            cellClass: 'base-data-grid__cell_centered no-change-modal',
                            headerCellClass: 'base-data-grid__cell_centered',
                            width: 70,
                            minWidth: 70,
                            resizable: false,
                            draggable: false,
                            clickDisable: true,
                            visible: hasRole('orderDeleteAgreementProlongation'),
                            formatter: ({ row }) => {
                                if (!row.dtDelete && row.isLast > 0) {
                                    return (
                                        <WorkplaceBtnActions menuContainer={container}>
                                            <ProlongationAgreementDeletePopover
                                                id={row.id}
                                                refetchQueryKey={['orderAgreementProlongations']}
                                            />
                                        </WorkplaceBtnActions>
                                    );
                                }

                                return <></>;
                            },
                        },
                    ]}
                >
                    <DataGrid<LoanAgreementProlongation>
                        reactDataGreedProps={{
                            defaultColumnOptions: {
                                sortable: false,
                                resizable: true,
                            },
                        }}
                        rows={data.prolongationAgreements}
                        blockSize={
                            data.prolongationAgreements.length && !isFetching
                                ? 35 + data.prolongationAgreements.length * 35
                                : 90
                        }
                        loading={isFetching}
                    />
                </GridColumnsConfigProvider>
            </Card>
            <hr />
        </>
    ) : (
        <></>
    );
};

export default AgreementProlongations;
